import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { facebookSendPost, facebookUserGet } from '../../api/queue-control';
import { HasClassName } from '../../component-utils';
import { useSelector } from '../../hook-utils';
import { CallState, FacebookComment, FacebookPost, Media } from '../../model';
import { actions } from '../../store';
import { selectDateFormating, selectMyself } from '../../store/reducers/config';
import { getFacebookPostById } from '../../store/reducers/facebook';
import { getParkedMedia } from '../../store/reducers/localSettings';
import { selectExtension } from '../../store/reducers/queueAgents';
import { formatRelativeDateTime } from '../AgentsTable';
import Button from '../Button';
import { ReactComponent as ParkForIcon } from '../icons/callcontrol/hold-for.svg';
import { ReactComponent as ParkIcon } from '../icons/callcontrol/queue-hold.svg';
import { ReactComponent as ParkResumeIcon } from '../icons/callcontrol/queue-resume.svg';
import { ReactComponent as DefaultUser } from '../icons/facebook-user.svg';
import './FacebookView.scss';


interface Props extends HasClassName {
  media?: Media
  activityId: string
  queueId: string
}

export default function FacebookView({ className, activityId, queueId, media }: Props) {
  const dispatch = useDispatch()
  const facebook = useSelector(s => getFacebookPostById(s, activityId))
  const [userName, setUserName] = useState('')
  const readOnly = media === undefined

  //get facebook
  useEffect(() => {
    if (!facebook)
      dispatch(actions.fetchFacebookPostsRequest(activityId, queueId))
  }, [activityId, dispatch, facebook, queueId])

  //get username if not there
  // not storing in data as too much hasle as only happens ever now and then
  useEffect(() => {
    let mounted = true

    if (facebook && facebook.userName !== '') {
      setUserName(facebook.userName)
      return
    }

    load()
    return () => { mounted = false }

    async function load() {
      if (!facebook) return
      if (!userName) {
        const name = await facebookUserGet(activityId, queueId, facebook.userId, '', '')
        if (!mounted) { return }
        setUserName(name)
      }
    }

  }, [activityId, facebook, queueId, userName])


  if (!facebook) { return <>loading...</> }

  return <div key={'page-' + facebook.page.pageId} className={classNames('FacebookView', className && className)}>
    {media && <div className='MediaControl toolbar'>
      <ParkCallControlButtons media={media} />
    </div>}
    <div className='header'>
      <div className='header-one'><span className='header-one-bold'>Page: </span>{facebook.page.name}</div>
      <div className='header-one'><span className='header-one-bold'>Visitor: </span>{userName}</div>
    </div>
    <div className='body'>
    {facebook.posts.map(p => <FacebookPostView key={'post-' + p.postId} id={activityId} queueId={queueId} post={p} readOnly={readOnly} />)}
    </div>
  </div>

}

function FacebookPostView({ id, queueId, post, readOnly }: { id: string, queueId: string, post: FacebookPost, readOnly: boolean }) {
  const postId = post.postId
  const pageId = post.pageId
  const [text, setText] = useState('')
  const [show, setShow] = useState(false)
  const format = useSelector(selectDateFormating)
  const dispatch = useDispatch()
  const [userName, setUserName] = useState('')

  // get username if not there
  // not storing in data as too much hasle as only happens ever now and then
  useEffect(() => {
    let mounted = true

    if (post.userName !== '') {
      setUserName(post.userName)
      return () => { mounted = true }
    }
    load()
    return () => { mounted = false }

    async function load() {
      if (!id || !queueId || !post.userId || !postId) { return }
      const name = await facebookUserGet(id, queueId, post.userId, postId, '')
      if (!mounted) { return }
      setUserName(name)
    }

  }, [dispatch, id, post.userId, post.userName, postId, queueId])

  if (!format) return null

  return <div className='grid'>
    <div>
      <div className='profile' onClick={() => window.open('https://www.facebook.com/' + postId)} title='View post in Facebook' ><FacebookProfile key={'profile-' + post.postId} userId={post.userId} pageId={post.pageId} /></div>
      {post.comments && post.comments.length > 0 && <div className='profile-dash-grid'>
        <div>&nbsp;</div>
        <div>&nbsp;</div>
      </div>
      }
    </div>
    <div className='content'>
      <div className='header'>
        <div>{userName}</div>
        <div className='status'>{post.unRead ? 'New' : ''}</div>
      </div>
      <div className='date'>{formatRelativeDateTime(post.createdAtUtc, format.dateTimeShortFormat, format.shortTimeFormat)}</div>
      <div className='post'>{post.text}</div>

      {post.type === 'photo' && post.linkUrl !== '' && post.linkPicture !== '' && <div className='photo'><a href={post.linkUrl} rel='noreferrer noopener'><img src={post.linkPicture} /></a></div>}
      {post.type !== 'photo' && post.linkUrl !== '' && post.linkPicture !== '' && <div className='preview'><a href={post.linkUrl} rel='noreferrer noopener'><img src={post.linkPicture} title={post.linkDescription} /></a><div>{post.linkTitle}</div><div>{post.linkDescription}</div></div>}

      <div className='links'> <span onClick={(e) => setShow(!show)}>Comment</span> </div>
      <div className={classNames(!show && 'hide')}><textarea key={(post.postId + post.pageId)} onChange={e => {
        setText(e.currentTarget.value)
      }
      } value={text} /></div>
      <div className={classNames(!show && 'hide', 'buttons')}>
        <Button primary key={('bt-send-' + post.postId)} disabled={readOnly} onClick={() => {
          if(!text) {
            dispatch(actions.showClientNotification('Replies cannot be empty'))
            return
          }

          facebookSendPost(id, queueId, pageId, postId, '', text)
          setShow(false)
          setText('')
        }}>Send</Button>

        <Button key={('bt-cancel-' + post.postId)} disabled={readOnly} onClick={() => {
          setShow(false)
          setText('')
        }}>Cancel</Button>

      </div>
    </div >
    {post.comments && post.comments.sort(sortByCreationTime).map((comment, index) => {
      const last = index === post.comments.length - 1
      return <React.Fragment key={index + '-' + comment.commentId}>
        <div className={classNames('dash-grid', last && 'dash-grid-last')}>
          <div>&nbsp;</div>
          <div>&nbsp;</div>
          <div>&nbsp;</div>
          <div>&nbsp;</div>
        </div>
        <div className='content'>
          <FacebookCommentView id={id} queueId={queueId} pageId={pageId} comment={comment} last={last} index={index} readOnly={readOnly} />
        </div>
      </React.Fragment>
    })}
  </div>
}

function FacebookCommentView({ id, queueId, pageId, comment, last, index, readOnly }: { id: string, queueId: string, pageId: string, comment: FacebookComment, last: boolean, index: number, readOnly: boolean }) {
  const [text, setText] = useState('')
  const postId = comment.postId
  const commentId = comment.commentId
  const [show, setShow] = useState(false)
  const format = useSelector(selectDateFormating)
  const dispatch = useDispatch()
  const [userName, setUserName] = useState('')

  // get username if not there
  // not storing in data as too much hasle as only happens ever now and then
  useEffect(() => {
    let mounted = true

    if (comment.userName !== '') {
      setUserName(comment.userName)
      return () => { mounted = true }
    }
    load()
    return () => { mounted = false }

    async function load() {
      const name = await facebookUserGet(id, queueId, comment.userId, comment.postId, comment.commentId)
      if (!mounted) { return }
      setUserName(name)
    }

  }, [dispatch, id, queueId, comment.userId, comment.postId, comment.commentId, comment.userName])


  if (!format) return null

  return <div className='grid grid-sub'>
    <div>
      <div className='profile' onClick={() => window.open('https://www.facebook.com/' + postId)} title='View post on Facebook' ><FacebookProfile userId={comment.userId} pageId={pageId} /></div>
      {comment.replies && comment.replies.length > 0 && comment.parentCommentId === '' && <div className='profile-dash-grid'>
        <div>&nbsp;</div>
        <div>&nbsp;</div>
      </div>}
    </div>

    <div className='content'>
      <div className='header'>
        <div>{userName}</div>
        <div className='status'>{comment.unRead ? 'New' : ''}</div>
      </div>
      <div className='date'>{formatRelativeDateTime(comment.createdAtUtc, format.dateTimeShortFormat, format.shortTimeFormat)}</div>
      <div className='post'>{comment.text}</div>
      {comment.attachmentType === 'photo' && comment.attachmentUrl !== '' && comment.attachmentImage !== '' && <div className='photo'><a href={comment.attachmentUrl} rel='noreferrer noopener'><img src={comment.attachmentImage} /></a></div>}
      {comment.attachmentType !== 'photo' && comment.attachmentUrl !== '' && comment.attachmentImage !== '' && <div className='preview'><div><a href={comment.attachmentUrl} rel='noreferrer noopener'><img src={comment.attachmentImage} title={comment.attachmentDescription} /></a></div><div><b>{comment.attachmentTitle}</b><br />{comment.attachmentDescription}</div></div>}
      <div className='links'> <span onClick={(e) => setShow(!show)}>Reply</span></div>
      <div className={classNames(!show && 'hide')}><textarea key={(comment.postId + comment.commentId)} onChange={e => {
        setText(e.currentTarget.value)
      }
      } value={text} />
      </div>

      <div className={classNames(!show && 'hide', 'buttons')}>
        {<Button primary key={('bt' + comment.commentId)} disabled={readOnly} onClick={() => {
          if(!text) {
            dispatch(actions.showClientNotification('Replies cannot be empty'))
            return
          }
          facebookSendPost(id, queueId, pageId, postId, commentId, text)
          setShow(false)
          setText('')
        }}>Send</Button>}

        {<Button key={('bt-cancel-' + comment.commentId)} disabled={readOnly} onClick={() => {
          setShow(false)
          setText('')
        }}>Cancel</Button>}

      </div>

    </div>

    {comment.replies && comment.replies.sort(sortByCreationTime).map((reply, index) => {
      const last = index === comment.replies.length - 1
      return <React.Fragment key={index + '-' + reply.commentId}>
        <div>
          <div className={classNames(!last && 'dash-grid', last && 'dash-grid-sub-last')}>
            <div>&nbsp;</div>
            <div>&nbsp;</div>
            <div>&nbsp;</div>
            <div>&nbsp;</div>
          </div>
        </div>
        <div className='content'>
          <FacebookCommentView id={id} queueId={queueId} pageId={pageId} comment={reply} last={last} index={index} readOnly={readOnly} />
        </div>
      </React.Fragment>
    })}
  </div>
}

function FacebookProfile({ pageId, userId }: { pageId: string, userId: string }) {
  if (pageId === userId) {
    return <div><img src={'https://graph.facebook.com/' + pageId + '/picture?square'} /></div>
  }
  else {
    return <div><DefaultUser /></div>
  }
}

export function ParkCallControlButtons({ media }: { media: Media }) {
  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const parked = useSelector(getParkedMedia)
  const dispatch = useDispatch()

  if (!myId || !extension) return null

  return <>{media && media.status === CallState.ExtensionAnswered && media.deviceId === myId.id && <button onClick={() => dispatch(actions.ccQueueHold(myId.id, '', media.id, media.mediaType))} title='Park'>
    <ParkIcon />
  </button>}
    {media && media.status === CallState.ExtensionAnswered && media.deviceId === myId.id && <button onClick={() => {
      dispatch(actions.openSmartSearch('hold-for',
        extension.status,
        extension.subStatus,
        myId.id,
        false,
        media))

    }} title='Park for...'>
      <ParkForIcon />
    </button>}
    {media && (media.status === CallState.QueueHold || (media.status === CallState.WaitingForWrapup && parked.find(id => id === media.id))) && <button onClick={() => dispatch(actions.ccQueueResume(myId.id, [media]))} title='Unpark'>
      <ParkResumeIcon /></button>}
  </>
}

function sortByCreationTime(a: FacebookComment, b: FacebookComment) {
  return a.createdAtUtc === b.createdAtUtc ? 0 : a.createdAtUtc > b.createdAtUtc ? 1 : -1
}
